import { useSnackbar } from 'notistack'
import React, { useState } from 'react'
import { useSelector } from 'react-redux'
import { useHistory, useParams } from 'react-router-dom'
import { AiOutlineDown, AiOutlineInbox } from 'react-icons/ai'
import { Checkbox, Drawer, Dropdown, Menu, Tabs, Upload, message } from 'antd'

import { usePet, useToggle, notifOps, server } from '../../modules'

import Datepicker from "../reusable/datePicker"
import Confirm from '../reusable/confirm'

const { Dragger } = Upload
const { TabPane } = Tabs

const shaDocumentTypes = [
    'CLAIM_FORM',
    'PREAUTH_FORM',
    'DISCHARGE_SUMMARY',
    'PRESCRIPTION',
    'LAB_ORDER',
    'INVOICE',
    'BIO_DETAILS',
    'IMAGING_ORDER',
    'OTHER',
    'FINAL_BILL',
    'LAB_RESULTS',
    'DEATH_NOTICE',
    'THEATRE_NOTES',
]

const UploadAttachment = ({ reload, onPerform = () => null }) => {
    const [title, setTitle] = useState()
    const [desc, setDesc] = useState()
    const [file, setFile] = useState([])
    const [type, setType] = useState()
    const [shaDocumentType, setShaDocumentType] = useState()
    const [interventionCode, setInterventionCode] = useState()

    const [visible, toggleVisible] = useToggle(true)

    const { enqueueSnackbar } = useSnackbar()

    const { params } = useSelector(state => state.application)

    const history = useHistory()
    const { id } = useParams()

    const props = {
        name: 'referral',
        fileList: file,
        action: 'https://www.mocky.io/v2/5cc8019d300000980a055e76',
        onChange: info => {
            let { fileList } = info
            fileList = fileList.slice(-1)
            let temp = fileList[0]

            const isOK = temp?.type === 'image/jpeg' || temp.type === 'image/png' || temp.type === 'application/pdf'
            if (!isOK) {
                enqueueSnackbar('Please upload either an image or pdf', notifOps('warning'))
                return false
            }
    
            let isLarge = (temp?.size / 1024 / 1024) > params?.maxFileUploadSize
            if (isLarge)
                enqueueSnackbar(`Please upload a file smaller than ${params?.maxFileUploadSize} MB`, notifOps('warning'))
            
            if (isOK && !isLarge) {
                setFile(fileList)
                setType(temp?.type)
            }
        },
        beforeUpload: () => false,
        onDrop: (e) => {
            console.log('Dropped files', e.dataTransfer.files);
        },
    }

    const close = () => {
        toggleVisible()
        setTimeout(() => history.goBack(), 100)
    }

    const onComplete = ({ data }, notif, ops) => {
        onPerform(data)
        notif('Attachment has been uploaded', ops('success'))
        
        setTimeout(close, 300)
    }

    const [{ loading }, upload] = usePet('uploadPatientAttachment', {
        patient: id,
        title,
        desc,
        type,
        shaDocumentType,
        interventionCode,
        attachment: file[0]?.originFileObj
    }, { hasFile: true, notify: true, callback: onComplete, manual: true })

    return (
        <Drawer
            closable
            maskClosable
            visible={visible}
            onClose={close}
            footer={
                <div className="w-full text-right">
                    <button onClick={ () => Confirm({
                        title: 'Upload Attachment?',
                        message: 'Please confirm before uploading this document?',
                        onOk: upload,
                        okText: 'Upload Attachment',
                    }) } className="text-white hisani-btn-green-light font-medium rounded-lg text-sm px-5 py-2.5 text-center">Upload Attachment</button>
                </div>
            }
            width={500}>
            <p className="font-extrabold text-3xl mb-0">Upload Attachment</p>
            <p className="text-gray-500 text-xs mt-0"></p>
            <div className="divide-y divide-light-gray-400">
                <div className="w-full mt-3 mb-6 md:mb-0">
                    <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2" htmlFor="grid-name">
                        Title
                    </label>
                    <input
                        onChange={ e => setTitle(e.target.value) }
                        className="appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
                        id="grid-title"
                        type="text"
                        value={title}
                        name="title"
                        placeholder="Attachment Title" />
                </div>
                <div className="w-full mt-3 mb-6 md:mb-0">
                    <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2" htmlFor="grid-name">
                        Description
                    </label>
                    <textarea
                        rows="6"
                        onChange={ e => setDesc(e.target.value) }
                        className="appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
                        id="grid-description"
                        type="text"
                        value={desc}
                        name="description"
                        placeholder="Attachment Description" />
                </div>
                <div className="w-full pt-3 mb-6 md:mb-0">
                    <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2" htmlFor="grid-sha-document-type">
                        SHA Document Type
                    </label>
                    <select
                        onChange={ e => setShaDocumentType(e.target.value || undefined) }
                        className="appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
                        id="grid-sha-document-type"
                        value={shaDocumentType ?? ''}>
                        <option value="">Not claim-tagged</option>
                        {
                            shaDocumentTypes.map(option =>
                                <option key={ option } value={ option }>{ option }</option>)
                        }
                    </select>
                    <p className="text-xs text-gray-500 mt-0 mb-3">Optional, but needed if this attachment should be reused in SHA claim upload.</p>
                </div>
                <div className="w-full mt-3 mb-6 md:mb-0">
                    <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2" htmlFor="grid-intervention-code">
                        Intervention Code
                    </label>
                    <input
                        onChange={ e => setInterventionCode(e.target.value) }
                        className="appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
                        id="grid-intervention-code"
                        type="text"
                        value={interventionCode}
                        name="interventionCode"
                        placeholder="Optional SHA intervention code for this attachment" />
                </div>
                <div className="w-full pt-3 mb-6 md:mb-0">
                    <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2" htmlFor="grid-name">
                        Upload File
                    </label>
                    <Dragger {...props}>
                        <p className="ant-upload-drag-icon flex justify-center">
                            <AiOutlineInbox size={40} />
                        </p>
                        <p className="ant-upload-text">Click or drag file to this area to upload</p>
                        <p className="ant-upload-hint">Please select a pdf or image file (pdf/jpg/jpeg/png)</p>
                    </Dragger>
                </div>
            </div>
        </Drawer>
    )
}

export default UploadAttachment
